import { useApi } from '@/api';
import { TableSkeleton, VaultName } from '@/components';

import {
  CryptoIcon,
  CurrencyValue,
  DataTable,
  EtherScanPictogram,
  Flex,
  RelativeDateTime
} from '@blockanalitica/ui';

import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useParams } from 'react-router-dom';

export default function EventsTable({
  collateralSymbol,
  collateralAddress,
  pagination,
  sorting,
  filters
}) {
  const { ilk } = useParams();
  const [data, error] = useApi({
    url: `/groups/core/ilks/${ilk}/events/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      operation: filters.filtersApplied.eventType
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'Vault ID',
        getCell: (row) => (
          <VaultName cdp={row.cdp} urn={row.urn} to={`../cdps/${row.urn}`} />
        ),
        columnSize: ['minmax(110px, auto)', 'minmax(150px, auto)'],
        sortingField: 'cdp'
      },
      {
        header: 'Event',
        getCell: (row) => row.operation,
        sortingField: 'operation'
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Debt
            <CryptoIcon name="DAI" size="0" />
          </Flex>
        ),
        getCell: (row) => (
          <CurrencyValue
            value={row.dart}
            options={{
              nullable: null,
              trend: true,
              iconSize: '0'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'dart'
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Collateral
            <CryptoIcon
              name={collateralSymbol}
              address={collateralAddress}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => (
          <CurrencyValue
            value={row.dink}
            options={{
              trend: true,
              nullable: null,
              iconSize: '0'
            }}
          />
        ),
        getCellBottom: (row) => (
          <CurrencyValue
            color="muted"
            fontSize="-1"
            value={row.dink * row.collateral_price}
            options={{
              currency: 'USD',
              nullable: null
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'dink'
      },
      {
        header: 'Time',
        getCell: (row) => (
          <Flex gap="3xs" color="muted">
            <RelativeDateTime value={row.datetime} />
            <a
              href={`https://etherscan.io/tx/${row.tx_hash}`}
              target="_blank"
              rel="noreferrer">
              <EtherScanPictogram size="0" />
            </a>
          </Flex>
        ),
        sortingField: 'order_index',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ];

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.order_index}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    );
  }
  return <TableSkeleton />;
}
